import React, { createContext, useContext, useState, useEffect } from 'react';
import { ProcessWorkflowState, ProcessType } from '../../../../../Services/WorkflowServicesTypes';
import WorkflowServices from '../../../../../Services/WorkflowServices';
import SystemServices from '../../../../../Services/SystemServices';
import { useCallbackApiPolling } from '../../../../../Hooks/useApiPolling';
import relatedProcesses from './RelatedProcessTypes';

const ProcessStatusContext = createContext<ProcessStatusContextType | undefined>(undefined);

function useProcessStatusContext() {
	const context = useContext(ProcessStatusContext);
	if (!context) {
		throw new Error('useProcessStatusContext must be used within a MyContextProvider');
	}
	return context;
}

export interface ProcessStatusProviderProps {
	children?: React.ReactNode;
	processType: ProcessType;
	accountId: string;
	$http: any;
	$rootScope: any;
	$timeout: any;
}

const pollingIntervalInMilliseconds = 6000;

const ProcessStatusProvider: React.FC<ProcessStatusProviderProps> = ({
	children,
	processType,
	$http,
	$rootScope,
	$timeout,
	accountId
}) => {

	const [canView, setCanView] = useState<boolean>(false);
	const [canRequest, setCanRequest] = useState<boolean>(false);
	const [processWorkflowState, setProcessWorkflowState] = useState<ProcessWorkflowState | undefined>(undefined);
	const [requestCreationRequested, setRequestCreationRequested] = useState<boolean>(false);
	const [requestDetailsRequested, setRequestDetailsRequested] = useState<boolean>(false);
	const [availableProcessTypes, setAvailableProcessTypes] = useState<{ processType: ProcessType, related: ProcessType[] }[]>([]);
	const [syncIsDisabled, setSyncIsDisabled] = useState<boolean>(false);

	const systemServices = new SystemServices($rootScope, $timeout);

	const [fakeGetProcessCallNumber, setFakeGetProcessCallNumber] = useState<number>(0);
	const [useFake] = useState<boolean>(false);
	const incr = () => setFakeGetProcessCallNumber(i => i + 1);
	const services = new WorkflowServices(accountId, $http, { getProcessCallNumber: fakeGetProcessCallNumber, useFake, incr });

	useEffect(() => {
		services.getAccountConfiguration(success => {
			const { permissions, availableProcesses } = success;
			const canRequest = permissions.find(x => x.processType === processType)?.canRequest ?? false;
			const canView = canRequest;

			const available = relatedProcesses.map(p => ({ processType: p.processType, related: p.related.filter(x => availableProcesses.includes(x)) }));
			setAvailableProcessTypes(available);

			setCanView(canView);
			setCanRequest(canRequest);
		}, error => {
			console.log(error);
		});
	}, []);

	const { data: pollingData, invokeApi } = useCallbackApiPolling<ProcessWorkflowState>((callbackSuccess, callbackError) => {
		if (!canView)
			return;

		services.getProcess(processType, callbackSuccess, callbackError);
	}, null, null, pollingIntervalInMilliseconds);
	
	useEffect(() => {
		setProcessWorkflowState(pollingData || undefined);
	}, [pollingData]);

	useEffect(() => {
		invokeApi();
	}, [canView]);


	const handleStartSynchroRequested = () => {
		setRequestCreationRequested(true);
	};
	const handleShowDetailsRequested = () => {
		setRequestDetailsRequested(true);
	};
	const handleCloseRequestDetails = () => {
		setRequestDetailsRequested(false);
	};
	const handleCloseSynchroRequested = () => {
		setRequestCreationRequested(false);
	};

	const startSynchro = (relatedProcesses: ProcessType[]) => {
		setSyncIsDisabled(true);
		const processesToStart = [...relatedProcesses.map(
			x => ({
				processIdentifier: '',
				processType: x
			})
		), {
			processIdentifier: '',
			processType: processType
		}];
		services.accountWorkflowRequest(processesToStart, () => {
			invokeApi();
			setTimeout(() => setSyncIsDisabled(false), 6000);
		}, error => {
			console.log(error);
			systemServices.showError('An error occured while creating synchro request');
		});
	};

	const contextValue: ProcessStatusContextType = {
		processType: processType,
		availableProcessTypes: availableProcessTypes,
		processWorkflowState: processWorkflowState,
		canRequest: canRequest,
		canView: canView,
		syncIsDisabled: syncIsDisabled,
		requestCreationRequested: requestCreationRequested,
		requestDetailsRequested: requestDetailsRequested,
		handleCloseRequestDetails: handleCloseRequestDetails,
		handleStartSynchroRequested: handleStartSynchroRequested,
		handleShowDetailsRequested: handleShowDetailsRequested,
		handleCloseSynchroRequested: handleCloseSynchroRequested,
		startSynchro: startSynchro
	};
	return (
		<ProcessStatusContext.Provider value={contextValue}>
			{children}
		</ProcessStatusContext.Provider>
	);
};


type ProcessStatusContextType = {
	processType: ProcessType,
	availableProcessTypes: { processType: ProcessType, related: ProcessType[] }[],
	processWorkflowState: ProcessWorkflowState | undefined,
	canView: boolean,
	canRequest: boolean,
	syncIsDisabled: boolean,
	requestCreationRequested: boolean,
	requestDetailsRequested: boolean,
	handleCloseRequestDetails: () => void,
	handleStartSynchroRequested: () => void,
	handleShowDetailsRequested: () => void,
	handleCloseSynchroRequested: () => void,
	startSynchro: (relatedProcesses: ProcessType[]) => void
};


export default ProcessStatusProvider;
export { useProcessStatusContext };
